'use client'
import { Icon } from '@graftcode/design-system'

import { AppVariant } from '../enums/appVariant.enum'

import { APP_NAMES, ENVIRONMENT_PREFIX } from './config.const'
import type { Environment, FallbackKey } from './config.type'
import { detectEnvironment } from './utils/detect-environment.util'

function getFallbackUrl(appName: FallbackKey, environment: Environment): string {
  const baseName = APP_NAMES[appName]
  const envPrefix = ENVIRONMENT_PREFIX[environment]
  return envPrefix ? `https://${baseName}.${envPrefix}.graftcode.com` : `https://${baseName}.graftcode.com`
}

// When Vision runs locally, navigation links point to production to avoid hydration mismatches
function isVisionLocalMode(): boolean {
  return process.env.NEXT_PUBLIC_VISION_MODE === 'local'
}

/** Resolves URL from env var, ignoring unreplaced BAKED_ placeholders. */
function getUrl(envValue: string | undefined, fallbackKey: FallbackKey): string {
  if (isVisionLocalMode()) {
    return getFallbackUrl(fallbackKey, 'production')
  }
  if (envValue && !envValue.startsWith('BAKED_')) {
    return envValue
  }
  return getFallbackUrl(fallbackKey, detectEnvironment())
}

export function getNavigationUrls() {
  return {
    portal: getUrl(process.env.NEXT_PUBLIC_WEB_PORTAL_URL, 'portal'),
    vision: getUrl(process.env.NEXT_PUBLIC_WEB_VISION_URL, 'vision'),
    modules: getUrl(process.env.NEXT_PUBLIC_WEB_MODULES_URL, 'modules'),
    gateway: getUrl(process.env.NEXT_PUBLIC_WEB_GATEWAYS_URL, 'gateway'),
    insights: getUrl(process.env.NEXT_PUBLIC_WEB_INSIGHTS_URL, 'insights'),
    academy: getUrl(process.env.NEXT_PUBLIC_WEB_ACADEMY_URL, 'academy'),
    docs: getUrl(process.env.NEXT_PUBLIC_WEB_DOCS_URL, 'docs'),
    conference: getUrl(process.env.NEXT_PUBLIC_WEB_CONFERENCE_URL, 'conference'),
  }
}

export function getNavigationItems(currentApp?: AppVariant | string) {
  const urls = getNavigationUrls()
  const items = [
    {
      id: 'portal',
      icon: Icon.GCPortal,
      label: 'Portal',
      url: urls.portal,
    },
    {
      id: 'vision',
      icon: Icon.GCVision,
      label: 'Vision',
      url: urls.vision,
    },
    {
      id: 'modules',
      icon: Icon.GCModules,
      label: 'Modules',
      url: urls.modules,
    },
    {
      id: 'gateway',
      icon: Icon.GCGateway,
      label: 'Gateway',
      url: urls.gateway,
    },
    {
      id: 'insights',
      icon: Icon.GCInsights,
      label: 'Insights',
      url: urls.insights,
      disabled: true,
    },
    {
      id: 'academy',
      icon: Icon.GCAcademy,
      label: 'Academy',
      url: urls.academy,
      disabled: false,
    },
    {
      id: 'conference',
      icon: Icon.GCConference,
      label: process.env.NEXT_PUBLIC_CONFERENCE_NAV_LABEL ?? 'Conference',
      url: urls.conference,
    },
  ]

  if (currentApp !== AppVariant.CONFERENCE && currentApp !== 'conference') {
    return items.filter((item) => item.id !== 'conference')
  }

  return items
}
